import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import colors, { useColors } from 'app/utils/colors';
import TwoFactorSetup from 'app/views/Settings/TwoFactorSetup';

function ShieldKeyIcon({ primaryColor }) {
  const strokeColor = primaryColor || 'var(--theme-primary, #f95f1f)';
  return (
    <div style={{
      ...styles.iconCircle,
      background: primaryColor ? `color-mix(in srgb, ${primaryColor} 8%, #ffffff)` : 'linear-gradient(135deg, #FFF5F2 0%, #FFEBE4 100%)',
      border: primaryColor ? `1px solid color-mix(in srgb, ${primaryColor} 20%, transparent)` : '1px solid rgba(241, 90, 36, 0.15)',
      boxShadow: primaryColor ? `0 8px 16px -4px color-mix(in srgb, ${primaryColor} 15%, transparent)` : '0 8px 16px -4px rgba(241, 90, 36, 0.12)',
    }}>
      <svg
        width="40"
        height="40"
        viewBox="0 0 24 24"
        fill="none"
        stroke={strokeColor}
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" fill={primaryColor ? `color-mix(in srgb, ${primaryColor} 10%, transparent)` : 'rgba(241, 90, 36, 0.08)'} />
        <circle cx="12" cy="11" r="3" stroke={strokeColor} strokeWidth="2" />
        <path d="M12 14v4" stroke={strokeColor} strokeWidth="2" strokeLinecap="round" />
      </svg>
    </div>
  );
}

export default function PostRecoveryModal() {
  const dynamicColors = useColors();
  const primaryColor = dynamicColors.primary || 'var(--theme-primary, #f95f1f)';
  const { t } = useTranslation('auth');
  const [showModal, setShowModal] = useState(false);
  const [showResetSetup, setShowResetSetup] = useState(false);

  useEffect(() => {
    const wasRecovered = sessionStorage.getItem('signedInViaRecovery');
    if (wasRecovered === 'true') {
      setShowModal(true);
    }
  }, []);

  const handleDismiss = () => {
    sessionStorage.removeItem('signedInViaRecovery');
    setShowModal(false);
  };

  const handleOpenReset = () => {
    setShowModal(false);
    setShowResetSetup(true);
  };

  const handleCloseResetSetup = () => {
    setShowResetSetup(false);
    sessionStorage.removeItem('signedInViaRecovery');
  };

  if (!showModal && !showResetSetup) return null;

  return (
    <>
      {/* ── Post Recovery Recommendation Modal ── */}
      {showModal && (
        <div style={styles.overlay} onClick={handleDismiss}>
          <div style={styles.modalCard} onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              aria-label="Close"
              onClick={handleDismiss}
              style={styles.closeBtn}
            >
              ×
            </button>

            <ShieldKeyIcon primaryColor={primaryColor} />

            <div style={styles.contentArea}>
              <span style={{
                ...styles.badge,
                color: primaryColor,
                background: primaryColor ? `color-mix(in srgb, ${primaryColor} 10%, transparent)` : 'rgba(241, 90, 36, 0.08)',
              }}>
                🔒 {t('twoFactor.recovery.eyebrow') || 'Account Recovery'}
              </span>

              <h3 style={styles.title}>
                {t('twoFactor.postRecovery.title') || "You're signed in using a recovery code"}
              </h3>

              <p style={styles.subtitle}>
                {t('twoFactor.postRecovery.subtitle') ||
                  'If you lost your phone or no longer have access to your authenticator, we recommend setting up Two-Factor Authentication again on your new device.'}
              </p>

              <div style={styles.actions}>
                <button
                  type="button"
                  style={{
                    ...styles.primaryBtn,
                    background: primaryColor,
                    boxShadow: primaryColor ? `0 4px 12px color-mix(in srgb, ${primaryColor} 25%, transparent)` : '0 4px 12px rgba(241, 90, 36, 0.25)',
                  }}
                  onClick={handleOpenReset}
                >
                  ⚡ {t('twoFactor.postRecovery.resetBtn') || 'Reset 2FA Now'}
                </button>

                <button
                  type="button"
                  style={styles.secondaryBtn}
                  onClick={handleDismiss}
                >
                  {t('twoFactor.postRecovery.dismissBtn') || 'Not now'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── 2FA Reset Setup Modal ── */}
      {showResetSetup && (
        <TwoFactorSetup
          show={showResetSetup}
          onClose={handleCloseResetSetup}
          initialStep="reset"
        />
      )}
    </>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(17, 24, 39, 0.65)',
    backdropFilter: 'blur(6px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1100,
    padding: '20px',
    animation: 'fadeIn 0.2s ease-out',
  },
  modalCard: {
    position: 'relative',
    background: '#ffffff',
    borderRadius: '16px',
    boxShadow: '0 20px 40px -15px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.05)',
    width: '100%',
    maxWidth: '460px',
    padding: '36px 32px 32px',
    textAlign: 'center',
    boxSizing: 'border-box',
    overflow: 'hidden',
  },
  closeBtn: {
    position: 'absolute',
    top: '16px',
    right: '18px',
    background: 'transparent',
    border: 'none',
    fontSize: '24px',
    lineHeight: 1,
    color: '#9CA3AF',
    cursor: 'pointer',
    padding: '4px',
    transition: 'color 0.15s',
  },
  iconCircle: {
    width: '72px',
    height: '72px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 20px',
  },
  contentArea: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '12px',
    fontWeight: '600',
    padding: '4px 12px',
    borderRadius: '999px',
    marginBottom: '14px',
    letterSpacing: '0.3px',
    textTransform: 'uppercase',
  },
  title: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#111827',
    margin: '0 0 12px',
    lineHeight: '1.35',
  },
  subtitle: {
    fontSize: '14px',
    color: '#4B5563',
    lineHeight: '1.6',
    margin: '0 0 28px',
    maxWidth: '380px',
  },
  actions: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    width: '100%',
  },
  primaryBtn: {
    width: '100%',
    padding: '13px 20px',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '10px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'transform 0.15s, box-shadow 0.15s',
  },
  secondaryBtn: {
    width: '100%',
    padding: '12px 20px',
    background: '#FFFFFF',
    color: '#6B7280',
    border: '1.5px solid #E5E7EB',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background 0.15s, border-color 0.15s',
  },
};
